// Profit structure waterfall — decomposes net P&L into its components like TradingView's
// "Profit structure" chart: Total profit (+green) → Total loss (−red) → Commission (−orange)
// → Total P&L (blue, net of all the above).
//
// Hand-rolled SVG so we don't pull in a charting dep for a 4-bar viz. ~120 LOC including
// SVG construction.

import type { BacktestResponseDto } from "../../api/types";

interface Props {
  result: BacktestResponseDto;
  height?: number;
}

interface Decomp {
  label: string;
  grossProfit: number;
  grossLoss: number;        // negative when displayed
  commission: number;       // negative when displayed
  total: number;
}

function pickDecomp(result: BacktestResponseDto): Decomp | null {
  if (result.strategy) {
    return {
      label: "Netting",
      grossProfit: result.strategy.grossProfit,
      grossLoss: -result.strategy.grossLoss,
      commission: -result.strategy.commissionPaid,
      total: result.strategy.totalReturn,
    };
  }
  if (result.merged) {
    return {
      label: "Merged",
      grossProfit: result.merged.grossProfit,
      grossLoss: -result.merged.grossLoss,
      commission: -result.merged.commissionPaid,
      total: result.merged.totalReturn,
    };
  }
  if (result.long) {
    return {
      label: "Long",
      grossProfit: result.long.grossProfit,
      grossLoss: -result.long.grossLoss,
      commission: -result.long.commissionPaid,
      total: result.long.totalReturn,
    };
  }
  if (result.short) {
    return {
      label: "Short",
      grossProfit: result.short.grossProfit,
      grossLoss: -result.short.grossLoss,
      commission: -result.short.commissionPaid,
      total: result.short.totalReturn,
    };
  }
  return null;
}

export function ProfitStructureWaterfall({ result, height = 220 }: Props) {
  const d = pickDecomp(result);
  if (!d) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No profit structure data.
      </div>
    );
  }

  // Bar values; we plot magnitudes and color-code direction.
  const bars = [
    { label: "Total profit", value: d.grossProfit, color: "#3fb950" },
    { label: "Total loss",   value: d.grossLoss,   color: "#f85149" },
    { label: "Commission",   value: d.commission,  color: "#d29922" },
    { label: "Total P&L",    value: d.total,       color: d.total >= 0 ? "#58a6ff" : "#f85149" },
  ];

  // SVG layout: equal-width bars, baseline at 50% of height, max bar height = 40% of height.
  const W = 320, H = height;
  const padX = 16, padTop = 16, padBottom = 32;
  const baseline = H - padBottom;
  const maxBarH = baseline - padTop;
  const barWidth = (W - padX * 2) / bars.length;
  // Scale by the most-positive (any) magnitude so bars are comparable.
  const peak = Math.max(...bars.map((b) => Math.abs(b.value)), 0.0001);

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        Profit structure <span style={{ color: "#8b949e", fontWeight: 400, fontSize: 11 }}>({d.label})</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" width="100%" height={H} style={{ display: "block" }}>
        {/* Baseline */}
        <line x1={padX} x2={W - padX} y1={baseline} y2={baseline} stroke="#30363d" strokeWidth={1} />
        {bars.map((b, i) => {
          const cx = padX + barWidth * (i + 0.5);
          const absH = (Math.abs(b.value) / peak) * maxBarH;
          const y = b.value >= 0 ? baseline - absH : baseline;
          return (
            <g key={b.label}>
              <rect
                x={cx - barWidth * 0.3}
                y={y}
                width={barWidth * 0.6}
                height={absH}
                fill={b.color}
                opacity={0.85}
              />
              {/* Value label */}
              <text
                x={cx}
                y={b.value >= 0 ? y - 4 : y + absH + 12}
                fill="#c9d1d9"
                fontSize={11}
                textAnchor="middle"
                fontFamily="monospace"
              >
                {fmtPct(b.value)}
              </text>
              {/* X-axis label */}
              <text
                x={cx}
                y={H - 8}
                fill="#8b949e"
                fontSize={10}
                textAnchor="middle"
              >
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}
